import { useState, useEffect, useRef, useContext } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useToast, useDisclosure } from "@chakra-ui/react";
import { useNavigate } from "react-router";
import { EDSHOP_IMAGE_RESOURCES, EDSHOP_AUDIO_RESOURCES, EDSHOP_PDF_RESOURCES, EDSHOP_PPT_RESOURCES, EDSHOP_VIDEOS } from "../../graphql/queries";
import { DELETE_RESOURCE, DELETE_VIMEO_VIDEO } from "../../graphql/mutations";
import { PublisherAndUserContext } from "../../contexts/PublisherAndUserContext";
import { useVideoUploader } from "../../hooks/useVideoUploader";
import { useBreadcrumbs } from '../../contexts/BreadcrumbProvider';

export const useResource = ({type}) => {
    const { user, publisher } = useContext(PublisherAndUserContext);
    const { uploadVideo } = useVideoUploader();
    const [loading, setLoading] = useState(false);
    const [hasMorePage, setHasMorePage] = useState(false);
    const [resources, setResources] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterType, setFilterType] = useState(type);
    const toast = useToast();
    const endOfListRef = useRef(null);
    const deleteResourceRef = useRef({});
    const navigate = useNavigate();
    const { addBreadcrumb } = useBreadcrumbs();

    const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure();
    const { isOpen: isVideoUploadOpen, onOpen: onVideoUploadOpen, onClose: onVideoUploadClose } = useDisclosure();
    const {
        isOpen: isDeleteModalOpen,
        onOpen: onDeleteModelOpen,
        onClose: onDeleteModelClose,
    } = useDisclosure();
    const {
        isOpen: isTokenExhaustOpen,
        onOpen: onTokenExhaustOpen,
        onClose: onTokenExhaustClose,
      } = useDisclosure();
    const {
        isOpen: isTokenExhaustUpgradeToProOpen,
        onOpen: onTokenExhaustUpgradeToProOpen,
        onClose: onTokenExhaustUpgradeToProClose,
    } = useDisclosure();

    const [getImageResources, { fetchMore: fetchMoreImages }] = useLazyQuery(EDSHOP_IMAGE_RESOURCES, {
        onCompleted: (data) => {
            setResources(data?.edshopifyImageResources?.resources);
            setHasMorePage(data?.edshopifyImageResources?.hasMore);
            setCurrentPage(data?.edshopifyImageResources?.hasMore ? currentPage + 1 : currentPage);
            setLoading(false);
        },
        onError: (err) => {
            setLoading(false);
            toast({
                position: "top-right",
                title: "Error fetching resources.",
                status: "error",
                duration: 2000,
                isClosable: true,
            });
            console.log(err)
        }
    });

    const [getAudioResources, { fetchMore: fetchMoreAudios }] = useLazyQuery(EDSHOP_AUDIO_RESOURCES, {
        onCompleted: (data) => {
            setResources(data?.edshopifyAudioResources?.resources);
            setHasMorePage(data?.edshopifyAudioResources?.hasMore);
            setCurrentPage(data?.edshopifyAudioResources?.hasMore ? currentPage + 1 : currentPage);
            setLoading(false);
        },
        onError: (err) => {
            setLoading(false);
            toast({
                position: "top-right",
                title: "Error fetching resources.",
                status: "error",
                duration: 2000,
                isClosable: true,
            });
            console.log(err)
        }
    });

    const [getPdfResources, { fetchMore: fetchMorePdfs }] = useLazyQuery(EDSHOP_PDF_RESOURCES, {
        onCompleted: (data) => {
            setResources(data?.edshopifyPdfResources?.resources);
            setHasMorePage(data?.edshopifyPdfResources?.hasMore);
            setCurrentPage(data?.edshopifyPdfResources?.hasMore ? currentPage + 1 : currentPage);
            setLoading(false);
        },
        onError: (err) => {
            setLoading(false);
            toast({
                position: "top-right",
                title: "Error fetching resources.",
                status: "error",
                duration: 2000,
                isClosable: true,
            });
            console.log(err)
        }
    });

    const [getPptResources, { fetchMore: fetchMorePpts }] = useLazyQuery(EDSHOP_PPT_RESOURCES, {
        onCompleted: (data) => {
            setResources(data?.edshopifyPptResources?.resources);
            setHasMorePage(data?.edshopifyPptResources?.hasMore);
            setCurrentPage(data?.edshopifyPptResources?.hasMore ? currentPage + 1 : currentPage);
            setLoading(false);
        },
        onError: (err) => {
            setLoading(false);
            toast({
                position: "top-right",
                title: "Error fetching resources.",
                status: "error",
                duration: 2000,
                isClosable: true,
            });
            console.log(err)
        }
    });

    const [getVideos, { fetchMore: fetchMoreVideos }] = useLazyQuery(EDSHOP_VIDEOS, {
        onCompleted: (data) => {
            setResources(data?.edshopifyVideos?.videos);
            setHasMorePage(data?.edshopifyVideos?.hasMore);
            setCurrentPage(data?.edshopifyVideos?.hasMore ? currentPage + 1 : currentPage);
            setLoading(false);
        },
        onError: (err) => {
            setLoading(false);
            toast({
                position: "top-right",
                title: "Error fetching resources.",
                status: "error",
                duration: 2000,
                isClosable: true,
            });
            console.log(err)
        }
    });

    const [deleteResource, { loading: deleting }] = useMutation(DELETE_RESOURCE, {
        update(cache, { data: { edshopifyDeleteResource } }, { variables }) {
            if (!edshopifyDeleteResource) return;

            const { id, folderType, fileName } = variables?.where;

            if (folderType === "images") {
                cache.modify({
                    fields: {
                        edshopifyImageResources(existingEdshopifyImageResourcesRefs = {}, { readField }) {
                            const updatedResources = existingEdshopifyImageResourcesRefs.resources.filter(
                                (resourceRef) => readField("key", resourceRef) !== `${id}/${folderType}/${fileName}`
                            );
    
                            return {
                                ...existingEdshopifyImageResourcesRefs,
                                resources: updatedResources,
                            };
                        },
                    },
                });
            }

            if (folderType === "audios") {
                cache.modify({
                    fields: {
                        edshopifyAudioResources(existingEdshopifyAudioResourcesRefs = {}, { readField }) {
                            const updatedResources = existingEdshopifyAudioResourcesRefs.resources.filter(
                                (resourceRef) => readField("key", resourceRef) !== `${id}/${folderType}/${fileName}`
                            );
    
                            return {
                                ...existingEdshopifyAudioResourcesRefs,
                                resources: updatedResources,
                            };
                        },
                    },
                });
            }

            if (folderType === "pdfs") {
                cache.modify({
                    fields: {
                        edshopifyPdfResources(existingEdshopifyPdfResourcesRefs = {}, { readField }) {
                            const updatedResources = existingEdshopifyPdfResourcesRefs.resources.filter(
                                (resourceRef) => readField("key", resourceRef) !== `${id}/${folderType}/${fileName}`
                            );
    
                            return {
                                ...existingEdshopifyPdfResourcesRefs,
                                resources: updatedResources,
                            };
                        },
                    },
                });
            }

            if (folderType === "ppts") {
                cache.modify({
                    fields: {
                        edshopifyPptResources(existingEdshopifyPptResourcesRefs = {}, { readField }) {
                            const updatedResources = existingEdshopifyPptResourcesRefs.resources.filter(
                                (resourceRef) => readField("key", resourceRef) !== `${id}/${folderType}/${fileName}`
                            );
    
                            return {
                                ...existingEdshopifyPptResourcesRefs,
                                resources: updatedResources,
                            };
                        },
                    },
                });
            }
            onDeletePopupClose();
            toast({
                position: "top-right",
                title: `Deleted ${fileName}`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const [deleteViemoVideo, { loading: deletingVimeoVideo }] = useMutation(DELETE_VIMEO_VIDEO, {
        update(cache, { data: { edshopifyDeleteVimeoVideo } }, { variables }) {
            if (!edshopifyDeleteVimeoVideo?.success) return;

            const { videoId } = variables?.where;
            cache.modify({
                fields: {
                    edshopifyVideos(existingedshopifyVideosRefs = {}, { readField }) {
                        const updatedVideos = existingedshopifyVideosRefs.videos.filter(
                            (videoRef) => readField('id', videoRef) !== videoId
                        );
                        return {
                            ...existingedshopifyVideosRefs,
                            videos: updatedVideos,
                        };
                    },
                },
            });
            onDeletePopupClose();
            toast({
                position: "top-right",
                title: `Deleted Video`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const handleVideoUpload = (data) => {
        uploadVideo(data);
        onVideoUploadClose();
    }

    const onDeletePopupClose = () => {
        deleteResourceRef.current = {};
        onDeleteModelClose();
    }

    const handleDelete = async () => {
        if (filterType !== 'videos') {
            const [id, folderType, fileName] = deleteResourceRef?.current?.key?.split('/')
            deleteResource({
                variables: {
                    where: {
                        id,
                        folderType,
                        fileName
                    }
                }
            })
        }
        if (filterType === 'videos') {
            const {id} = deleteResourceRef?.current
            deleteViemoVideo({
                variables: {
                    where: {
                        id: publisher?.id,
                        videoId: id
                    }
                }
            })
        }
    };

    const buyCredits = () => {
        addBreadcrumb("Credits Info", "/credits-info", true)
        navigate("/credits-info")
    }

    const upgradeToPro = () => {
        addBreadcrumb("Pricing Plan", "/pricingplan", true)
        navigate("/pricingplan")
    }

    const updateFilterType = (e) => {
        setFilterType(e.target.value);
        setCurrentPage(1);
        setLoading(true);
        setResources([]);
        if (e.target.value === "videos") {
            getVideos({
                variables: {
                    where: {
                        id: publisher?.id,
                        pageSize: 50,
                        page: 1,
                    }
                }
            });
            return true;
        }
        if (e.target.value === "images") {
            getImageResources({
                variables: {
                    where: {
                        id: publisher?.id,
                        pageSize: 50,
                        page: 1,
                    }
                }
            });
            return true;
        }
        if (e.target.value === "audios") {
            getAudioResources({
                variables: {
                    where: {
                        id: publisher?.id,
                        pageSize: 50,
                        page: 1,
                    }
                }
            });
            return true;
        }
        if (e.target.value === "ppts") {
            getPptResources({
                variables: {
                    where: {
                        id: publisher?.id,
                        pageSize: 50,
                        page: 1,
                    }
                }
            });
            return true;
        }
        getPdfResources({
            variables: {
                where: {
                    id: publisher?.id,
                    pageSize: 50,
                    page: 1,
                }
            }
        });
    };

    useEffect(() => {      
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasMorePage && filterType === 'images') {
                    setLoading(true);
                    fetchMoreImages({
                        variables: {
                            pageSize: 50,
                            page: currentPage,
                        }
                    })
                }
                if (entry.isIntersecting && hasMorePage && filterType === 'audios') {
                    setLoading(true);
                    fetchMoreAudios({
                        variables: {
                            pageSize: 50,
                            page: currentPage,
                        }
                    })
                }
                if (entry.isIntersecting && hasMorePage && filterType === 'pdfs') {
                    setLoading(true);
                    fetchMorePdfs({
                        variables: {
                            pageSize: 50,
                            page: currentPage,
                        }
                    })
                }
                if (entry.isIntersecting && hasMorePage && filterType === 'ppts') {
                    setLoading(true);
                    fetchMorePpts({
                        variables: {
                            pageSize: 50,
                            page: currentPage,
                        }
                    })
                }
                if (entry.isIntersecting && hasMorePage && filterType === 'videos') {
                    setLoading(true);
                    fetchMoreVideos({
                        variables: {
                            pageSize: 50,
                            page: currentPage,
                        }
                    })
                }
            },
            { threshold: 1.0 } // Trigger when the element is fully visible
        );

        if (endOfListRef.current) {
            observer.observe(endOfListRef.current);
        }

        return () => {
            if (endOfListRef.current) {
                observer.unobserve(endOfListRef?.current);
            }
        };
    }, [fetchMoreImages, fetchMoreAudios, fetchMorePdfs, fetchMorePpts, hasMorePage, currentPage, filterType, fetchMoreVideos]);

    useEffect(() => {
        if (publisher?.id) {
            setLoading(true);
            if(type === "images") {
                getImageResources({
                    variables: {
                        where: {
                            id: publisher?.id,
                            pageSize: 50,
                            page: currentPage,
                        }
                    }
                });
            } else if(type === "videos") {
                getVideos({
                variables: {
                    where: {
                        id: publisher?.id,
                        pageSize: 50,
                        page: 1,
                    }
                }
            });
            } else if(type === "pdfs") {
                getPdfResources({
                variables: {
                    where: {
                        id: publisher?.id,
                        pageSize: 50,
                        page: 1,
                    }
                }
            });
            } else if (type === "ppts") {
                getPptResources({
                    variables: {
                        where: {
                            id: publisher?.id,
                            pageSize: 50,
                            page: 1,
                        }
                    }
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publisher?.id])

    return {
        loading,
        resources,
        filterType,
        deleteResourceRef,
        endOfListRef,
        deleting,
        deletingVimeoVideo,
        updateFilterType,
        isUploadOpen,
        onUploadOpen,
        onUploadClose,
        isVideoUploadOpen,
        onVideoUploadOpen,
        onVideoUploadClose,
        isDeleteModalOpen,
        onDeleteModelOpen,
        onDeleteModelClose,
        onDeletePopupClose,
        handleVideoUpload,
        handleDelete,
        setFilterType,
        user,
        publisher,
        isTokenExhaustOpen,
        onTokenExhaustOpen,
        onTokenExhaustClose,
        isTokenExhaustUpgradeToProOpen,
        onTokenExhaustUpgradeToProOpen,
        onTokenExhaustUpgradeToProClose,
        buyCredits,
        upgradeToPro,
    }
}